/**
 * ScrollablePdfReader — wraps react-pdf in a scrollable container so we can
 * RELIABLY detect when the customer has read the entire document.
 *
 * Background (Fabrizio req. 11/06/2026):
 *   The previous ContractModal embedded the PDF in a raw <iframe>, which on
 *   desktop browsers delegates rendering to the browser's native PDF plugin
 *   (Chrome PDFium, Firefox PDF.js worker). Those plugins DO NOT expose
 *   scroll events to the parent document — even when the iframe is
 *   same-origin via a blob: URL — so our scroll-end detector never fired
 *   and the safety 30s fallback timeout was unlocking the "I have read"
 *   checkbox without the user scrolling at all.
 *
 * Solution:
 *   Render every page of the PDF as a <canvas> sequence inside a div whose
 *   overflow:auto produces a REAL HTML scrollbar. The parent then gets
 *   `scrollContainerRef` and the read-confirmation hook listens to scroll
 *   events on it (HTML-standard scroll = guaranteed events).
 *
 * Mobile is unchanged: the parent decides whether to render this reader or
 * fall back to the legacy iframe + 15s countdown (mobile PDF viewers behave
 * the same way as desktop plugins, AND text rendering via canvas can be
 * laggy on low-end Android — keeping the iframe is safer).
 */
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Loader2, AlertCircle } from "lucide-react";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Worker pdf.js servito localmente dal frontend (`/public/pdf.worker.min.js`)
// per rispettare la CSP "script-src 'self'" — unpkg.com è bloccato.
//
// IMPORTANTE: estensione `.js` (NON `.mjs`) perché in produzione il proxy
// statico di Emergent serve i file con estensioni non standard come
// `application/octet-stream` (è successo l'11/06/2026: Chrome bloccava
// il worker con "Strict MIME type checking is enforced"). Tutti i server
// statici moderni mappano automaticamente `.js → application/javascript`,
// quindi rinominare a `.js` risolve il problema senza richiedere config
// custom del proxy / CDN.
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

export const ScrollablePdfReader = forwardRef(({
  fileUrl,
  height = "60vh",
  minHeight = 400,
  errorFallbackHref,
  errorFallbackLabel = "Apri PDF in nuova scheda",
  loadingLabel = "Caricamento PDF...",
  errorLabel = "Impossibile caricare il PDF.",
  retryLabel = "Riprova",
}, externalScrollRef) => {
  const [numPages, setNumPages] = useState(null);
  const [pdfError, setPdfError] = useState(false);
  // Retry counter: bumping this forces react-pdf's <Document> to remount
  // (we pass it as part of the `key`), which triggers a fresh fetch even
  // when the fileUrl hasn't changed. Used by the "Riprova" button below.
  const [retryNonce, setRetryNonce] = useState(0);

  // We need an internal ref so the ResizeObserver can measure the container
  // width even when the parent hasn't passed `externalScrollRef`. The
  // forwarded ref is synced with the internal one inside `attachRef` below
  // (Fabrizio 11/06/2026 bug — the privacy PDF was rendered wider than its
  // GDPR-modal container, clipping ~60px of text on both edges. Reading
  // window.innerWidth doesn't match the real container width when the
  // reader is mounted inside a centred / max-w-2xl modal).
  const internalRef = useRef(null);
  const attachRef = useCallback((node) => {
    internalRef.current = node;
    if (typeof externalScrollRef === "function") externalScrollRef(node);
    else if (externalScrollRef) externalScrollRef.current = node;
  }, [externalScrollRef]);

  // Responsive page width — measured from the ACTUAL scroll container so
  // the PDF always fits, regardless of which modal/section it lives in.
  // 24px of horizontal padding gives a small breathing room and avoids
  // touching the scrollbar.
  const [pageWidth, setPageWidth] = useState(640);

  useEffect(() => {
    const recalc = () => {
      const node = internalRef.current;
      if (!node) return;
      const w = node.clientWidth - 24;  // padding for scrollbar + breathing
      if (w > 0) setPageWidth(Math.max(280, Math.min(900, w)));
    };
    recalc();
    let ro;
    if (typeof ResizeObserver !== "undefined" && internalRef.current) {
      ro = new ResizeObserver(recalc);
      ro.observe(internalRef.current);
    }
    window.addEventListener("resize", recalc);
    return () => {
      window.removeEventListener("resize", recalc);
      if (ro) ro.disconnect();
    };
  }, [fileUrl]);

  // Fabrizio 11/06/2026 bug report ("change UI to UK → Impossibile caricare il PDF"):
  // when the parent re-renders for ANY reason (typical case: customer toggles
  // the UI language picker which cascades a country/translations refresh down
  // to GDPRPreferences), this component would keep `pdfError=true` from the
  // first failed attempt — react-pdf's <Document> is *not* re-rendered because
  // we gate it behind `!pdfError`, so the recovery fetch never happens.
  // FIX: reset error/state whenever `fileUrl` or `retryNonce` changes. We also
  // expose a "Riprova" button so the customer can self-recover from a network
  // blip without refreshing the whole flow.
  useEffect(() => {
    setPdfError(false);
    setNumPages(null);
  }, [fileUrl, retryNonce]);

  const onLoadSuccess = useCallback(({ numPages: n }) => {
    setNumPages(n);
    setPdfError(false);
  }, []);
  const onLoadError = useCallback((err) => {
    // eslint-disable-next-line no-console
    console.warn("[ScrollablePdfReader] PDF load failed:", err?.message || err);
    setPdfError(true);
  }, []);
  const handleRetry = useCallback(() => setRetryNonce((n) => n + 1), []);

  return (
    <div
      ref={attachRef}
      className="w-full rounded-lg border border-gray-700 bg-white overflow-y-auto"
      style={{ height, minHeight: `${minHeight}px` }}
      data-testid="scrollable-pdf-reader"
    >
      {pdfError ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3 text-gray-700">
          <AlertCircle className="w-7 h-7" />
          <p className="text-sm">{errorLabel}</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={handleRetry}
              className="text-sm px-3 py-1 rounded border border-gray-400 hover:bg-gray-100"
              data-testid="scrollable-pdf-reader-retry"
            >
              {retryLabel}
            </button>
            {errorFallbackHref && (
              <a
                href={errorFallbackHref}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 underline"
                data-testid="scrollable-pdf-reader-error-fallback"
              >
                {errorFallbackLabel}
              </a>
            )}
          </div>
        </div>
      ) : (
        <Document
          // `key` ties react-pdf's internal lifecycle to (fileUrl + retryNonce)
          // so a manual retry fully remounts the document, bypassing any
          // sticky internal state from the previous failure.
          key={`${fileUrl || ''}__${retryNonce}`}
          file={fileUrl}
          onLoadSuccess={onLoadSuccess}
          onLoadError={onLoadError}
          loading={
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-700">
              <Loader2 className="w-7 h-7 animate-spin" />
              <p className="text-sm">{loadingLabel}</p>
            </div>
          }
        >
          {Array.from({ length: numPages || 0 }, (_, i) => (
            <div key={i + 1} className="flex justify-center py-2 border-b border-gray-200 last:border-b-0">
              <Page
                pageNumber={i + 1}
                width={pageWidth}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                loading={
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
                  </div>
                }
              />
            </div>
          ))}
        </Document>
      )}
    </div>
  );
});

ScrollablePdfReader.displayName = "ScrollablePdfReader";
